import { useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEventHandler, type MouseEventHandler, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

interface TooltipProps {
  content: ReactNode
  children: ReactNode
  /** Posizione preferita: se non c'è spazio sufficiente viene ruotata
   * automaticamente sul lato opposto (o sul lato con più spazio). */
  position?: TooltipPosition
  className?: string
  onClick?: MouseEventHandler<HTMLSpanElement>
  onKeyDown?: KeyboardEventHandler<HTMLSpanElement>
}

const opposite: Record<TooltipPosition, TooltipPosition> = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

/** Distanza minima dal trigger + dai bordi viewport per considerare una
 * posizione "con spazio sufficiente". */
const GAP = 8
const VIEWPORT_MARGIN = 8

function fits(pos: TooltipPosition, space: Record<TooltipPosition, number>, tipW: number, tipH: number): boolean {
  switch (pos) {
    case 'top':
      return space.top >= tipH + GAP
    case 'bottom':
      return space.bottom >= tipH + GAP
    case 'left':
      return space.left >= tipW + GAP
    case 'right':
      return space.right >= tipW + GAP
  }
}

/** Sostituisce l'attributo title=""/nativo del browser (piccolo, giallo, ritardo fisso non
 * personalizzabile) con un tooltip coerente col design system. Appare su hover/focus con un
 * breve ritardo, si chiude subito su mouse-leave/blur - stessa ergonomia di un title nativo.
 *
 * Renderizzato in portal su document.body con position: fixed, così non viene mai tagliato
 * da un antenato con overflow (sidebar collassata, modali, tabelle con scroll orizzontale).
 * La posizione preferita viene verificata contro lo spazio disponibile in viewport: se il
 * tooltip sforerebbe viene ruotato sul lato opposto o su quello con più spazio, e l'asse
 * trasversale viene ristretto ai bordi viewport (freccia contro-spostata di conseguenza). */
export function Tooltip({ content, children, position = 'top', className = '', onClick, onKeyDown }: TooltipProps) {
  const [visible, setVisible] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const [effectivePosition, setEffectivePosition] = useState<TooltipPosition>(position)
  const [arrowShift, setArrowShift] = useState(0)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const tipRef = useRef<HTMLSpanElement>(null)

  function supportsHover() {
    // Use the app's desktop breakpoint as well as input capabilities: a narrow
    // desktop window can still report a mouse hover, and tablets may report a
    // fine pointer when a trackpad is connected. Touch layouts should never show
    // tooltips when tapping or opening the sidebar.
    if (typeof window === 'undefined' || !window.matchMedia) return true
    const desktopHover = window.matchMedia(
      '(min-width: 992px) and (hover: hover) and (pointer: fine)',
    ).matches
    const touchCapable = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0
    return desktopHover && !touchCapable
  }

  function show() {
    if (!supportsHover()) return
    setEffectivePosition(position)
    timeoutRef.current = setTimeout(() => setVisible(true), 300)
  }

  function hide() {
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    setVisible(false)
  }

  // Misura il tooltip reale e lo piazza in coordinate fixed: flip se la posizione
  // preferita non ci sta, clamp dell'asse trasversale ai bordi viewport.
  useLayoutEffect(() => {
    if (!visible) return

    function place() {
      const wrapper = wrapperRef.current
      const tip = tipRef.current
      if (!wrapper || !tip) return
      const tipW = tip.offsetWidth
      const tipH = tip.offsetHeight
      // Senza dimensioni misurabili (es. jsdom nei test) resta la preferita a 0,0.
      if (!tipW && !tipH) {
        setEffectivePosition(position)
        setCoords({ top: 0, left: 0 })
        setArrowShift(0)
        return
      }
      const rect = wrapper.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      const space: Record<TooltipPosition, number> = {
        top: rect.top - VIEWPORT_MARGIN,
        bottom: vh - rect.bottom - VIEWPORT_MARGIN,
        left: rect.left - VIEWPORT_MARGIN,
        right: vw - rect.right - VIEWPORT_MARGIN,
      }

      let next: TooltipPosition = position
      if (!fits(position, space, tipW, tipH)) {
        const opp = opposite[position]
        if (fits(opp, space, tipW, tipH)) {
          next = opp
        } else {
          const bySpace = (Object.keys(space) as TooltipPosition[]).sort((a, b) => space[b] - space[a])
          next = bySpace.find((p) => fits(p, space, tipW, tipH)) ?? bySpace[0]
        }
      }

      const cx = rect.left + rect.width / 2
      const cy = rect.top + rect.height / 2
      let top = 0
      let left = 0
      let shift = 0
      switch (next) {
        case 'top':
          top = rect.top - tipH - GAP
          left = Math.min(Math.max(cx - tipW / 2, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, vw - tipW - VIEWPORT_MARGIN))
          shift = cx - (left + tipW / 2)
          break
        case 'bottom':
          top = rect.bottom + GAP
          left = Math.min(Math.max(cx - tipW / 2, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, vw - tipW - VIEWPORT_MARGIN))
          shift = cx - (left + tipW / 2)
          break
        case 'left':
          left = rect.left - tipW - GAP
          top = Math.min(Math.max(cy - tipH / 2, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, vh - tipH - VIEWPORT_MARGIN))
          shift = cy - (top + tipH / 2)
          break
        case 'right':
          left = rect.right + GAP
          top = Math.min(Math.max(cy - tipH / 2, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, vh - tipH - VIEWPORT_MARGIN))
          shift = cy - (top + tipH / 2)
          break
      }
      setEffectivePosition(next)
      setCoords({ top, left })
      setArrowShift(shift)
    }

    place()
    // Riposiziona su scroll (capture: intercetta anche gli scroll interni di
    // sidebar/modali/tabelle) e resize/viewport: il fixed segue il trigger.
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [visible, position])

  const isVertical = effectivePosition === 'top' || effectivePosition === 'bottom'
  const arrowStyle: CSSProperties = {
    ...(effectivePosition === 'top' && { top: '100%', left: `calc(50% + ${arrowShift}px)`, transform: 'translateX(-50%)', borderTopColor: 'var(--color-tooltip-bg)' }),
    ...(effectivePosition === 'bottom' && { bottom: '100%', left: `calc(50% + ${arrowShift}px)`, transform: 'translateX(-50%)', borderBottomColor: 'var(--color-tooltip-bg)' }),
    ...(effectivePosition === 'left' && { left: '100%', top: `calc(50% + ${arrowShift}px)`, transform: 'translateY(-50%)', borderLeftColor: 'var(--color-tooltip-bg)' }),
    ...(effectivePosition === 'right' && { right: '100%', top: `calc(50% + ${arrowShift}px)`, transform: 'translateY(-50%)', borderRightColor: 'var(--color-tooltip-bg)' }),
  }

  return (
    <span
      ref={wrapperRef}
      className={`inline-flex ${className}`}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={onClick}
      onKeyDown={onKeyDown}
    >
      {children}
      {visible &&
        createPortal(
          <span
            ref={tipRef}
            role="tooltip"
            data-position={effectivePosition}
            className="pointer-events-none z-tooltip rounded-md px-2 py-1 text-xs whitespace-nowrap shadow-lg"
            style={{ position: 'fixed', top: coords.top, left: coords.left, backgroundColor: 'var(--color-tooltip-bg)', color: 'var(--color-tooltip-text)' }}
          >
            {content}
            <span style={arrowStyle} className="absolute h-0 w-0 border-4 border-transparent" />
          </span>,
          document.body
        )}
    </span>
  )
}
